/* eslint-disable @typescript-eslint/no-explicit-any */
import { ImageNeedingManualEdit } from "@/api/images-types";
import { RoleName } from "@/api/users-types";
import { ManualEditFilters } from "@/components/images/ManualEditFilters";
import { ManualImageUploadDialog } from "@/components/images/ManualImageUploadDialog";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { DragDropUpload } from "@/components/ui/DragDropUpload";
import { Link } from "@/components/ui/Link";
import { CircularProgressLoader } from "@/components/ui/loaders";
import { NoPermissions } from "@/components/ui/NoPermissions";
import { ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { ManualEditStateChip } from "@/components/ui/state-chips/ManualEditStateChip";
import { DEFAULT_PAGE_SIZE_XL } from "@/const";
import { useHasRole } from "@/hooks/use-has-role";
import { useRowCount } from "@/hooks/use-row-count";
import { useImagesNeedingManualEdit } from "@/queries/images/use-images-needing-manual-edit";
import {
  useUploadManualImage,
  useUploadManualMask,
} from "@/queries/images/use-upload-manual-image";
import {
  rejectionNotesColumn,
  rejectionReasonsColumn,
} from "@/utils/datagrid-utils";
import { prettyId } from "@/utils/pretty";
import {
  Alert,
  Box,
  Button,
  Snackbar as InfoSnackbar,
  Stack,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridFilterItem,
  GridSortModel,
} from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { handleDownloadImage } from "./utils";

const baseColumns: GridColDef<ImageNeedingManualEdit>[] = [
  {
    field: "id",
    headerName: "Image ID",
    width: 120,
    filterable: false,
    renderCell: (params) => {
      const { id } = params.row;
      return (
        <Link target="_blank" to={`/images/${id}`}>
          {id}
        </Link>
      );
    },
  },
  {
    field: "url",
    headerName: "Image",
    width: 240,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <img
          src={params.row.url}
          alt={"One of the images needing manual edit"}
          style={{ maxWidth: "200px", margin: "auto" }}
        />
      </Box>
    ),
  },
  {
    field: "isChildPresented",
    headerName: "Child Present",
    type: "boolean",
    width: 120,
    filterable: false,
  },
  {
    field: "episode.id",
    headerName: "Episode ID",
    filterable: false,
    renderCell: (params) => (
      <Link to={`/episodes/${params.row.episode?.id}`}>
        {params.row.episode?.id}
      </Link>
    ),
    width: 120,
  },
  {
    field: "manualEditState",
    headerName: "State",
    align: "center",
    headerAlign: "center",
    filterable: false,
    renderCell: (params) => (
      <ManualEditStateChip state={params.row.manualEditState} />
    ),
    width: 150,
  },
  rejectionReasonsColumn<ImageNeedingManualEdit>({
    sortable: false,
    filterable: false,
  }),
  rejectionNotesColumn<ImageNeedingManualEdit>({
    sortable: false,
    filterable: false,
  }),
  {
    field: "nextImage.url",
    headerName: "Fixed Image",
    width: 240,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        {params.row.nextImage && (
          <img
            src={params.row.nextImage.url}
            alt={"Fixed image"}
            style={{ maxWidth: "200px", margin: "auto" }}
          />
        )}
      </Box>
    ),
  },
];

export function ManualEditListPage() {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE_XL,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterItems, setFilterItems] = useState<GridFilterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  const {
    data,
    isLoading: areImagesLoading,
    error: imagesError,
  } = useImagesNeedingManualEdit({
    paginationModel,
    sortModel,
    filterModel: { items: filterItems },
  });

  const images = data?.items;

  const rowCount = useRowCount(data);

  const [uploadDialogState, setUploadDialogState] = useState<{
    imageId: string;
    episodeId: string;
    isChildPresented: boolean;
  } | null>(null);

  const {
    mutate: uploadManualImage,
    isPending: isImageUploading,
    error: imageUploadError,
  } = useUploadManualImage();

  const {
    mutate: uploadManualMask,
    isPending: isMaskUploading,
    error: maskUploadError,
  } = useUploadManualMask();

  const [globalUploadInfo, setGlobalUploadInfo] = useState<string | null>(null);

  const handleUpload = (
    imageId: string,
    episodeId: string | undefined,
    imageFile: File,
    maskFile?: File,
  ) => {
    uploadManualImage(
      { imageId, episodeId, imageFile, maskFile },
      {
        onSuccess: () => {
          setUploadDialogState(null);
        },
      },
    );
  };

  const handleGlobalUpload = async (files: File[]) => {
    setGlobalUploadInfo(null);
    files.sort((a, b) => a.name.localeCompare(b.name)); // sort files, so fixed images will be processed before masks

    const results: { file: string; success: boolean; error?: string }[] = [];
    setUploadProgress({ current: 0, total: files.length });

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const name = file.name;
      setUploadProgress({ current: i + 1, total: files.length });

      try {
        if (name.startsWith("fixed_")) {
          const imageId = name.slice(6, name.lastIndexOf("."));
          await new Promise<void>((resolve) => {
            uploadManualImage(
              { imageId, imageFile: file },
              {
                onSuccess: () => {
                  results.push({ file: name, success: true });
                  resolve();
                },
                onError: (error: any) => {
                  console.log("Error uploading image:", error);
                  results.push({
                    file: `fixed_${prettyId(imageId)}`,
                    success: false,
                    error: error.data?.error?.message ?? JSON.stringify(error),
                  });
                  resolve();
                },
              },
            );
          });
        } else if (name.startsWith("mask_")) {
          const imageId = name.slice(5, name.lastIndexOf("."));
          await new Promise<void>((resolve) => {
            uploadManualMask(
              { imageId, maskFile: file },
              {
                onSuccess: () => {
                  results.push({ file: name, success: true });
                  resolve();
                },
                onError: (error: any) => {
                  results.push({
                    file: `mask_${prettyId(imageId)}`,
                    success: false,
                    error: error.data?.error?.message ?? JSON.stringify(error),
                  });
                  resolve();
                },
              },
            );
          });
        } else {
          results.push({
            file: name,
            success: false,
            error:
              'Filename must start with "fixed_" or "mask_" followed by the original image ID.',
          });
        }
      } catch (error) {
        results.push({
          file: name,
          success: false,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    setUploadProgress(null);

    const successCount = results.filter((r) => r.success).length;
    const totalCount = results.length;
    const failedResults = results.filter((r) => !r.success);

    if (failedResults.length > 0) {
      const errorMessage = `Uploaded ${successCount}/${totalCount} files. Errors:\n${failedResults
        .map((r) => `${r.file}: ${r.error}`)
        .join(";\n")}`;
      setGlobalUploadInfo(errorMessage);
    } else {
      setGlobalUploadInfo(`Successfully uploaded ${successCount} files.`);
    }
  };

  const columns = useMemo(() => {
    const actionsColumn: GridColDef<ImageNeedingManualEdit> = {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={async () => {
                handleDownloadImage(
                  params.row.episode.id,
                  () => setLoading(true),
                  () => setLoading(false),
                );
              }}
            >
              Download
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                setUploadDialogState({
                  imageId: params.row.id,
                  episodeId: params.row.episode.id,
                  isChildPresented: params.row.isChildPresented,
                })
              }
            >
              Upload
            </Button>
          </Box>
        );
      },
    };
    return [...baseColumns, actionsColumn];
  }, []);

  const canView = useHasRole(RoleName.imageEditor);

  if (!canView) {
    return <NoPermissions />;
  }

  return (
    <Stack gap={2} mb={2}>
      <ManualEditFilters items={filterItems} onChange={setFilterItems} />
      <DataGrid
        columnHeaderHeight={46}
        columns={columns}
        loading={!!uploadProgress || loading || areImagesLoading}
        slots={{
          loadingOverlay: uploadProgress
            ? () => (
                <CircularProgressLoader
                  progress={
                    (uploadProgress.current / uploadProgress.total) * 100
                  }
                />
              )
            : undefined,
        }}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode={"server"}
        paginationModel={paginationModel}
        rows={images}
        rowCount={rowCount}
        rowHeight={120}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
      />

      <DragDropUpload
        label="Drag and drop fixed images here"
        loading={isImageUploading || isMaskUploading}
        onFilesUpload={handleGlobalUpload}
      />
      {uploadDialogState?.imageId && uploadDialogState?.episodeId && (
        <ManualImageUploadDialog
          open={!!uploadDialogState}
          onClose={() => setUploadDialogState(null)}
          imageId={uploadDialogState.imageId}
          episodeId={uploadDialogState.episodeId}
          isChildPresented={uploadDialogState.isChildPresented}
          onUpload={handleUpload}
          isUploading={isImageUploading || isMaskUploading}
        />
      )}
      {!uploadProgress && (
        <ApiErrorSnackbar error={imageUploadError || maskUploadError} />
      )}
      <ApiErrorAlert error={imagesError} />
      <InfoSnackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!globalUploadInfo}
        onClose={() => setGlobalUploadInfo(null)}
      >
        <Alert severity="info" onClose={() => setGlobalUploadInfo(null)}>
          {globalUploadInfo?.split("\n").map((line, index) => (
            <Typography sx={{ mb: 0.5 }} key={index}>
              {line}
            </Typography>
          ))}
        </Alert>
      </InfoSnackbar>
    </Stack>
  );
}
